import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { ScrollArea, ScrollBar } from "@/shared/ui/scroll-area";
import { Skeleton } from "@/shared/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/tooltip";
import { Check, X, Clock, Plus, AlertTriangle } from "lucide-react";
import { format, differenceInDays } from "date-fns";

interface Competency {
  id: string;
  name: string;
  description: string | null;
  color: string;
  is_global: boolean;
  certifiedCount?: number;
  coverage?: number;
}

interface FactoryUser {
  id: string;
  full_name: string | null;
  email: string;
  job_title: string | null;
  department: string | null;
}

interface UserCompetency {
  id: string;
  user_id: string;
  competency_id: string;
  certified_at: string;
  expires_at: string | null;
  status: string;
  competency: Competency;
}

interface CompetencyMatrixProps {
  competencies: Competency[];
  users: FactoryUser[];
  userCompetencies: UserCompetency[];
  isLoading: boolean;
  onAssign: (userId: string) => void;
}

export function CompetencyMatrix({
  competencies,
  users,
  userCompetencies,
  isLoading,
  onAssign,
}: CompetencyMatrixProps) {
  const [hoveredCell, setHoveredCell] = useState<{ userId: string; compId: string } | null>(null);

  const getCellStatus = (userId: string, compId: string) => {
    const uc = userCompetencies.find(
      (uc) => uc.user_id === userId && uc.competency_id === compId
    );
    if (!uc) return null;
    
    if (uc.status === "revoked") return "revoked";
    if (uc.status === "pending") return "pending";
    
    if (uc.expires_at) {
      const daysUntil = differenceInDays(new Date(uc.expires_at), new Date());
      if (daysUntil < 0) return "expired";
      if (daysUntil <= 30) return "expiring";
    }
    
    return "active";
  };

  const getUserCompetency = (userId: string, compId: string) => {
    return userCompetencies.find(
      (uc) => uc.user_id === userId && uc.competency_id === compId
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Skills Matrix</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (users.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Skills Matrix</CardTitle>
        </CardHeader>
        <CardContent className="text-center py-12">
          <p className="text-muted-foreground">No team members found in this factory</p>
        </CardContent>
      </Card>
    );
  }

  if (competencies.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Skills Matrix</CardTitle>
        </CardHeader>
        <CardContent className="text-center py-12">
          <p className="text-muted-foreground">No competencies defined yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Skills Matrix</span>
          <div className="flex items-center gap-4 text-xs font-normal">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-success" />
              <span>Certified</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-warning" />
              <span>Expiring</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-destructive" />
              <span>Expired</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-muted" />
              <span>None</span>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="w-full">
          <div className="min-w-max">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-card z-10 text-left p-3 border-b border-border min-w-[200px]">
                    Team Member
                  </th>
                  {competencies.map((comp) => (
                    <th
                      key={comp.id}
                      className="text-center p-2 border-b border-border min-w-[100px]"
                    >
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex flex-col items-center gap-1 cursor-help">
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0"
                                style={{ borderColor: comp.color, color: comp.color }}
                              >
                                {comp.name.length > 12
                                  ? comp.name.substring(0, 12) + "..."
                                  : comp.name}
                              </Badge>
                              <span className="text-[10px] text-muted-foreground">
                                {comp.certifiedCount || 0}/{users.length}
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-medium">{comp.name}</p>
                            {comp.description && (
                              <p className="text-xs text-muted-foreground max-w-[200px]">
                                {comp.description}
                              </p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </th>
                  ))}
                  <th className="p-2 border-b border-border min-w-[80px]"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-muted/30 transition-colors">
                    <td className="sticky left-0 bg-card z-10 p-3 border-b border-border">
                      <div>
                        <p className="font-medium text-sm">
                          {user.full_name || user.email.split("@")[0]}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {user.job_title || user.department || user.email}
                        </p>
                      </div>
                    </td>
                    {competencies.map((comp) => {
                      const status = getCellStatus(user.id, comp.id);
                      const uc = getUserCompetency(user.id, comp.id);
                      const isHovered =
                        hoveredCell?.userId === user.id && hoveredCell?.compId === comp.id;

                      return (
                        <td
                          key={comp.id}
                          className="text-center p-2 border-b border-border"
                          onMouseEnter={() =>
                            setHoveredCell({ userId: user.id, compId: comp.id })
                          }
                          onMouseLeave={() => setHoveredCell(null)}
                        >
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex justify-center">
                                  {status === "active" && (
                                    <div className="w-6 h-6 rounded-full bg-success/20 flex items-center justify-center">
                                      <Check className="w-3.5 h-3.5 text-success" />
                                    </div>
                                  )}
                                  {status === "expiring" && (
                                    <div className="w-6 h-6 rounded-full bg-warning/20 flex items-center justify-center">
                                      <AlertTriangle className="w-3.5 h-3.5 text-warning" />
                                    </div>
                                  )}
                                  {status === "expired" && (
                                    <div className="w-6 h-6 rounded-full bg-destructive/20 flex items-center justify-center">
                                      <X className="w-3.5 h-3.5 text-destructive" />
                                    </div>
                                  )}
                                  {status === "pending" && (
                                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                                      <Clock className="w-3.5 h-3.5 text-primary" />
                                    </div>
                                  )}
                                  {!status && (
                                    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                                      <span className="text-[10px] text-muted-foreground">-</span>
                                    </div>
                                  )}
                                </div>
                              </TooltipTrigger>
                              {uc && (
                                <TooltipContent>
                                  <div className="text-xs space-y-1">
                                    <p className="font-medium">{comp.name}</p>
                                    <p>
                                      Certified:{" "}
                                      {format(new Date(uc.certified_at), "MMM d, yyyy")}
                                    </p>
                                    {uc.expires_at && (
                                      <p>
                                        Expires:{" "}
                                        {format(new Date(uc.expires_at), "MMM d, yyyy")}
                                      </p>
                                    )}
                                    <p className="capitalize">Status: {uc.status}</p>
                                  </div>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                      );
                    })}
                    <td className="p-2 border-b border-border">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => onAssign(user.id)}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
